import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAetherContext } from '#pplx/frontend/aether/components/AetherProvider/useAetherContext.js';
import { getModalZIndex } from '#pplx/frontend/aether/components/internal/elevation/elevation.js';
import { useModalStackIndex } from '#pplx/frontend/aether/components/Modal/context/ModalStackContext.js';
import { ModalBackdrop } from '#pplx/frontend/aether/components/Modal/internal/ModalBackdrop.js';
import { ModalCloseButton } from '#pplx/frontend/aether/components/Modal/internal/ModalCloseButton.js';
import { ModalContent } from '#pplx/frontend/aether/components/Modal/internal/ModalContent.js';
import { getDefaultContentAriaProps, getModalChildrenState, getSideSheetContentAriaProps, } from '#pplx/frontend/aether/components/Modal/internal/ModalInternals.js';
import { useActivationOrderedOverlayStacking } from '#pplx/frontend/overlay-stacking/useOverlayStacking.js';
import * as Dialog from '@radix-ui/react-dialog';
import { useTransition } from '@react-spring/web';
import { useCallback, useMemo } from 'react';
export function SideSheetModal({ children, background = 'base', isOpen = true, onClose, dismissOnOutsideClick = true, onExitComplete, variant: _variant, ...restProps }) {
    const shouldUseActivationOrder = useActivationOrderedOverlayStacking();
    const { isMobileStyle } = useAetherContext();
    const stackIndex = useModalStackIndex();
    const zIndexStyle = useMemo(() => (shouldUseActivationOrder ? {} : { zIndex: getModalZIndex(stackIndex) }), [stackIndex, shouldUseActivationOrder]);
    const handleOpenChange = useCallback((open) => {
        if (!open) {
            onClose();
        }
    }, [onClose]);
    const { childArray, hasChildHeader, hasFooter, hasTitle } = useMemo(() => getModalChildrenState(children), [children]);
    const asBottomSheet = !!isMobileStyle;
    const hasCloseButton = true;
    const closeButton = _jsx(ModalCloseButton, {});
    const layoutState = useMemo(() => ({
        isMobile: !!isMobileStyle,
        chromeless: false,
        hasTitle,
        hasFooter,
        hasCloseButton,
    }), [isMobileStyle, hasTitle, hasFooter]);
    const contentAriaProps = useMemo(() => getSideSheetContentAriaProps(getDefaultContentAriaProps({ childArray, hasChildHeader }), restProps), [childArray, hasChildHeader, restProps]);
    const transitions = useTransition(isOpen, {
        from: { opacity: 0, scale: 0.8, x: 100, y: 100 },
        enter: { opacity: 1, scale: 1, x: 0, y: 0 },
        leave: { opacity: 0, scale: 0.8, x: 100, y: 100 },
        config: { tension: isMobileStyle ? 500 : 700, friction: 40, clamp: true },
        onDestroyed: (open) => {
            if (open) {
                onExitComplete?.();
            }
        },
    });
    return (_jsx(Dialog.Root, { open: isOpen, onOpenChange: handleOpenChange, children: transitions((style, open) => open && (_jsxs(Dialog.Portal, { forceMount: true, children: [_jsx(Dialog.Overlay, { asChild: true, forceMount: true, children: _jsx(ModalBackdrop, { opacity: style.opacity, zIndexStyle: zIndexStyle, isVisible: true, variant: asBottomSheet ? undefined : 'side-sheet' }) }), asBottomSheet ? (_jsxs(ModalContent, { variant: "bottom-sheet", background: background, y: style.y, contentStyle: zIndexStyle, contentAriaProps: contentAriaProps, dismissOnOutsideClick: dismissOnOutsideClick, layoutState: layoutState, children: [children, closeButton] })) : (_jsxs(ModalContent, { variant: "side-sheet", background: background, x: style.x, contentStyle: zIndexStyle, contentAriaProps: contentAriaProps, dismissOnOutsideClick: dismissOnOutsideClick, layoutState: layoutState, children: [children, closeButton] }))] }))) }));
}
//# sourceMappingURL=SideSheetModal.js.map